import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import DayDetailsPanel from './DayDetailsPanel'
import DaysListPanel from './DaysListPanel'
import { Menu, X, Info } from 'lucide-react'
import { waypointIcons } from '../config/waypointIcons'
import { buildElevationProfile } from '../utils/elevationProfile'

interface WalkActivity {
  id: number | string
  start_datetime: string
  end_datetime: string
  date: string
  strava_activity_id?: string
  distance_km: number
  elevation_gain_m: number
  elevation_loss_m?: number
  duration_hours: number
  activity_count?: number
  start_point: {
    type: 'Point'
    coordinates: [number, number]
    name: string
  }
  end_point: {
    type: 'Point'
    coordinates: [number, number]
    name: string
  }
  track: {
    type: 'LineString' | 'MultiLineString'
    coordinates: Array<[number, number, number]> | Array<Array<[number, number, number]>>
  }
  stats?: {
    max_elevation_m: number
    min_elevation_m: number
    avg_speed_kmh: number
    max_speed_kmh: number
  }
}

interface Waypoint {
  lat: number
  lng: number
  name?: string
  type?: 'checkpoint' | 'campsite' | 'endpoint' | 'water' | 'viewpoint' | 'other'
  color?: string
  order_index?: number
}

interface Walk {
  id: string
  name: string
  description?: string
  expected_km?: number
  total_distance_km?: number
  bounds?: {
    north: number
    south: number
    east: number
    west: number
  }
  waypoints?: Waypoint[]
}

interface MapComponentProps {
  walkId: string
}

const MapComponent = ({ walkId }: MapComponentProps) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [walkData, setWalkData] = useState<{ walk: Walk; activities: WalkActivity[] } | null>(null)
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [isDayDetailsPanelOpen, setIsDayDetailsPanelOpen] = useState(false)
  const [isDaysListPanelOpen, setIsDaysListPanelOpen] = useState(false)
  const [isLegendOpen, setIsLegendOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    if (!walkId) return

    fetchWalkData()
  }, [walkId])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (!walkData || !mapContainer.current) return

    initializeMap()

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [walkData])

  const fetchWalkData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(`/api/walk-activities?walkId=${walkId}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch walk data: ${response.statusText}`)
      }
      
      const data = await response.json()
      // The API returns activities grouped by date as `days` (combining multiple
      // activities that occur on the same day). The map shows days — one entry per
      // calendar day — matching the admin view, so use `days` not raw `activities`.
      setWalkData({ walk: data.walk, activities: data.days ?? data.activities ?? [] })
    } catch (err) {
      console.error('Error fetching walk data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load walk data')
    } finally {
      setLoading(false)
    }
  }

  const initializeMap = () => {
    if (!walkData || !mapContainer.current) return

    // Calculate initial center point from walk data
    let initialCenter = [115.8605, -31.9505] // Perth, WA default
    
    if (walkData.walk.bounds) {
      initialCenter = [
        (walkData.walk.bounds.east + walkData.walk.bounds.west) / 2,
        (walkData.walk.bounds.north + walkData.walk.bounds.south) / 2
      ]
    } else if (walkData.activities.length > 0) {
      const allCoords = walkData.activities.flatMap(activity => 
        activity.track.coordinates
      )
      if (allCoords.length > 0) {
        const bounds = new maplibregl.LngLatBounds()
        allCoords.forEach(coord => bounds.extend([coord[0], coord[1]]))
        initialCenter = bounds.getCenter().toArray()
      }
    } else if (walkData.walk.waypoints && walkData.walk.waypoints.length > 0) {
      // If no activities but there are waypoints, center on waypoints
      const bounds = new maplibregl.LngLatBounds()
      walkData.walk.waypoints.forEach(wp => bounds.extend([wp.lng, wp.lat]))
      initialCenter = bounds.getCenter().toArray()
    }

    // Initialize map at high zoom level (zoomed out)
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          'osm': {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors'
          }
        },
        layers: [
          {
            id: 'osm',
            type: 'raster',
            source: 'osm'
          }
        ]
      },
      center: initialCenter,
      zoom: 5 // Start zoomed out for nice zoom-in effect
    })

    // Add navigation controls (zoom and compass)
    map.current.addControl(new maplibregl.NavigationControl(), 'top-left')
    
    // Add custom zoom to walk button with proper styling
    class ZoomToWalkControl {
      _map: any
      _container: any
      
      onAdd(map: any) {
        this._map = map
        this._container = document.createElement('div')
        this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group'
        
        const button = document.createElement('button')
        button.className = 'maplibregl-ctrl-icon'
        button.type = 'button'
        button.title = 'Zoom to walk'
        button.setAttribute('aria-label', 'Zoom to walk')
        button.style.display = 'flex'
        button.style.alignItems = 'center'
        button.style.justifyContent = 'center'
        button.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>'
        button.onclick = () => fitMapToBounds(1000)
        
        this._container.appendChild(button)
        return this._container
      }
      
      onRemove() {
        this._container.parentNode.removeChild(this._container)
        this._map = undefined
      }
    }
    
    map.current.addControl(new ZoomToWalkControl(), 'top-left')

    map.current.on('load', () => {
      addWalkData()
      // Use setTimeout to ensure data is rendered before animating
      setTimeout(() => {
        fitMapToBounds()
      }, 100)
    })
  }

  const addWalkData = () => {
    if (!map.current || !walkData) return

    // Only add track data if there are activities
    if (walkData.activities.length > 0) {
      // Create GeoJSON for all tracks
      const tracksGeoJSON = {
        type: 'FeatureCollection' as const,
        features: walkData.activities.map((activity, index) => ({
          type: 'Feature' as const,
          properties: {
            activity_index: index + 1,
            date: activity.date,
            start_datetime: activity.start_datetime,
            distance_km: activity.distance_km,
            elevation_gain_m: activity.elevation_gain_m,
            duration_hours: activity.duration_hours,
            activity_count: activity.activity_count || 1,
            day_color: getColorForDay(index)
          },
          geometry: activity.track
        }))
      }

      // Create GeoJSON for start/end points
      const pointsGeoJSON = {
        type: 'FeatureCollection' as const,
        features: [
          // Start points
          ...walkData.activities.map((activity, index) => ({
            type: 'Feature' as const,
            properties: {
              type: 'start',
              activity_index: index + 1,
              date: activity.date,
              start_datetime: activity.start_datetime,
              name: activity.start_point.name,
              day_color: getColorForDay(index)
            },
            geometry: activity.start_point
          })),
          // End points
          ...walkData.activities.map((activity, index) => ({
            type: 'Feature' as const,
            properties: {
              type: 'end',
              activity_index: index + 1,
              date: activity.date,
              start_datetime: activity.start_datetime,
              name: activity.end_point.name,
              day_color: getColorForDay(index)
            },
            geometry: activity.end_point
          }))
        ]
      }

      // Add sources
      map.current!.addSource('tracks', {
        type: 'geojson',
        data: tracksGeoJSON
      })

      map.current!.addSource('points', {
        type: 'geojson',
        data: pointsGeoJSON
      })

      // White casing drawn underneath the coloured line, slightly wider, so a
      // subtle semi-transparent white border shows on each edge and the routes
      // stand out against the map.
      map.current!.addLayer({
        id: 'tracks-casing',
        type: 'line',
        source: 'tracks',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 7,
          'line-opacity': 0.5
        }
      })

      // Add track line layer (drawn on top of the casing)
      map.current!.addLayer({
        id: 'tracks',
        type: 'line',
        source: 'tracks',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': ['get', 'day_color'],
          'line-width': 4,
          'line-opacity': 0.8
        }
      })

      // Add wider invisible click layer for easier interaction
      map.current!.addLayer({
        id: 'tracks-clickable',
        type: 'line',
        source: 'tracks',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': 'transparent',
          'line-width': 20, // Much wider for easier clicking
          'line-opacity': 0
        }
      })

      // Start markers: solid dot in the day's route colour (white halo for contrast)
      map.current!.addLayer({
        id: 'start-points',
        type: 'circle',
        source: 'points',
        filter: ['==', ['get', 'type'], 'start'],
        paint: {
          'circle-radius': 6,
          'circle-color': ['get', 'day_color'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2
        }
      })

      // End markers: hollow ring (white centre, thick day-colour border) so start
      // vs end is told apart by shape, while colour still encodes the day.
      map.current!.addLayer({
        id: 'end-points',
        type: 'circle',
        source: 'points',
        filter: ['==', ['get', 'type'], 'end'],
        paint: {
          'circle-radius': 6,
          'circle-color': '#ffffff',
          'circle-stroke-color': ['get', 'day_color'],
          'circle-stroke-width': 3
        }
      })
    }

    // Add waypoint markers if they exist
    if (walkData.walk.waypoints && walkData.walk.waypoints.length > 0) {
      walkData.walk.waypoints.forEach((waypoint, index) => {
        const wpType = waypoint.type || 'checkpoint'
        const iconConfig = waypointIcons[wpType as keyof typeof waypointIcons] || waypointIcons.checkpoint
        
        const el = document.createElement('div')
        el.className = 'waypoint-marker'
        el.style.width = '32px'
        el.style.height = '32px'
        el.innerHTML = iconConfig.mapMarkerHtml(index)
        
        const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([waypoint.lng, waypoint.lat])
          .setPopup(
            new maplibregl.Popup({ offset: 25, closeButton: true, maxWidth: '200px' })
              .setHTML(`
                <div class="p-3">
                  <h3 class="font-bold text-base mb-1">${waypoint.name || `${iconConfig.label} ${index + 1}`}</h3>
                  <p class="text-sm text-gray-600">${iconConfig.label}</p>
                </div>
              `)
          )
          .addTo(map.current!)
      })
      
      // Add CSS to make the close button larger
      const style = document.createElement('style')
      style.textContent = `
        .maplibregl-popup-close-button {
          font-size: 24px !important;
          width: 30px !important;
          height: 30px !important;
          padding: 4px !important;
          line-height: 20px !important;
        }
      `
      document.head.appendChild(style)
    }

    // Add click handlers only if there are activities
    if (walkData.activities.length > 0) {
      // Add click handler for route sections to open day details panel
      map.current!.on('click', 'tracks-clickable', (e) => {
        if (!e.features?.[0]) return
        
        const feature = e.features[0]
        const props = feature.properties
        const dayIndex = props.activity_index - 1 // Convert to 0-based index
        
        setSelectedDay(dayIndex)
        setIsDayDetailsPanelOpen(true)
        setIsDaysListPanelOpen(false) // Close days list if open
      })

      // Add popups for end-point markers
      map.current!.on('click', 'end-points', (e) => {
      if (!e.features?.[0]) return
      
      const feature = e.features[0]
      const props = feature.properties
      
      // Find the corresponding activity data
      const activity = walkData.activities.find((_, index) => (index + 1) === props.activity_index)
      if (!activity) return
      
      new maplibregl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(`
          <div class="p-3">
            <h3 class="font-bold text-base mb-1">Day ${props.activity_index} - End</h3>
            <p class="text-sm text-gray-600 mb-2">${new Date(props.date).toLocaleDateString()}</p>
            <div class="space-y-1 text-sm">
              <div><span class="font-medium">Distance:</span> ${activity.distance_km}km</div>
              <div><span class="font-medium">Elevation gain:</span> ${activity.elevation_gain_m}m</div>
              <div><span class="font-medium">Duration:</span> ${Math.round(activity.duration_hours * 10) / 10}h</div>
              ${props.name ? `<div><span class="font-medium">Location:</span> ${props.name}</div>` : ''}
            </div>
          </div>
        `)
        .addTo(map.current!)
      })

      // Add cursor pointer on track hover
      map.current!.on('mouseenter', 'tracks-clickable', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })

      map.current!.on('mouseleave', 'tracks-clickable', () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })

      // Add cursor pointer on end-point hover
      map.current!.on('mouseenter', 'end-points', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })

      map.current!.on('mouseleave', 'end-points', () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })
    }
  }

  const fitMapToBounds = (duration: number = 2000) => {
    if (!map.current || !walkData) return

    // Calculate bounds from all data sources
    const bounds = new maplibregl.LngLatBounds()
    let hasData = false

    // Start with stored bounds if available
    if (walkData.walk.bounds) {
      bounds.extend([walkData.walk.bounds.west, walkData.walk.bounds.south])
      bounds.extend([walkData.walk.bounds.east, walkData.walk.bounds.north])
      hasData = true
    }

    // Add activity tracks to bounds (handle both LineString and MultiLineString)
    if (walkData.activities.length > 0) {
      const allCoords = walkData.activities.flatMap(activity => {
        if (activity.track.type === 'MultiLineString') {
          return activity.track.coordinates.flatMap(lineString => lineString)
        } else {
          return activity.track.coordinates || []
        }
      })

      if (allCoords.length > 0) {
        allCoords.forEach(coord => bounds.extend([coord[0], coord[1]]))
        hasData = true
      }
    }

    // Always extend bounds to include waypoints
    if (walkData.walk.waypoints && walkData.walk.waypoints.length > 0) {
      walkData.walk.waypoints.forEach(wp => {
        bounds.extend([wp.lng, wp.lat])
        hasData = true
      })
    }

    if (!hasData) {
      // No bounds available, return early
      return
    }

    // Fit map to calculated bounds
    map.current.fitBounds(bounds, {
      padding: isMobile ? 30 : 50,
      duration: duration
    })
  }

  const getColorForDay = (index: number): string => {
    const colors = [
      '#3b82f6', // blue
      '#ef4444', // red
      '#22c55e', // green
      '#f59e0b', // amber
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#06b6d4', // cyan
      '#84cc16', // lime
      '#f97316', // orange
      '#6366f1'  // indigo
    ]
    return colors[index % colors.length]
  }

  const handleDayClick = (dayIndex: number) => {
    setSelectedDay(dayIndex)
    setIsDayDetailsPanelOpen(true)
    setIsDaysListPanelOpen(false)
  }

  const handleClosePanels = () => {
    setIsDayDetailsPanelOpen(false)
    setIsDaysListPanelOpen(false)
    setSelectedDay(null)
  }

  const getSelectedDayData = () => {
    if (!walkData || selectedDay === null || selectedDay >= walkData.activities.length) {
      return null
    }
    
    const activity = walkData.activities[selectedDay]
    return {
      dayNumber: selectedDay + 1,
      color: getColorForDay(selectedDay),
      profile: buildElevationProfile(activity.track),
      date: activity.date,
      distance_km: activity.distance_km,
      elevation_gain_m: activity.elevation_gain_m,
      elevation_loss_m: activity.elevation_loss_m ?? 0,
      duration_hours: activity.duration_hours,
      start_point: {
        name: activity.start_point.name,
        coordinates: activity.start_point.coordinates
      },
      end_point: {
        name: activity.end_point.name,
        coordinates: activity.end_point.coordinates
      },
      stats: activity.stats || {
        max_elevation_m: 0,
        min_elevation_m: 0,
        avg_speed_kmh: activity.distance_km / activity.duration_hours || 0,
        max_speed_kmh: 0
      }
    }
  }

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button 
            onClick={fetchWalkData}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!walkData || (walkData.activities.length === 0 && (!walkData.walk.waypoints || walkData.walk.waypoints.length === 0))) {
    return (
      <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-gray-600">No track data available for this walk</p>
        </div>
      </div>
    )
  }

  return (
    <div className="absolute inset-0">
      <div ref={mapContainer} className="w-full h-full" />
      
      {/* Info panel toggle button - visible for all users */}
      {!isLegendOpen && (
        <div className="absolute top-4 right-4">
          <button
            onClick={() => setIsLegendOpen(!isLegendOpen)}
            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
            title="Toggle info panel"
          >
            <Info size={20} className="text-gray-700" />
          </button>
        </div>
      )}
      
      {/* Legend with Days List Toggle - Collapsible on mobile */}
      {isLegendOpen && (
        <div className="absolute top-4 right-4 sm:top-4 sm:right-4 bg-white rounded-lg shadow-lg p-3 sm:p-4 max-w-[90vw] sm:max-w-sm transition-all">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm truncate pr-2">{walkData.walk.name}</h3>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setIsDaysListPanelOpen(true)}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="View all days"
              >
                <Menu size={16} className="text-gray-600" />
              </button>
              <button
                onClick={() => setIsLegendOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="Close panel"
              >
                <X size={16} className="text-gray-600" />
              </button>
            </div>
          </div>
          
          {/* Display progress if expected_km is set */}
          {walkData.walk.expected_km && walkData.walk.total_distance_km !== undefined && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progress</span>
                <span className="font-medium">
                  {Math.round((walkData.walk.total_distance_km / walkData.walk.expected_km) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.round((walkData.walk.total_distance_km / walkData.walk.expected_km) * 100))}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{walkData.walk.total_distance_km}km completed</span>
                <span>{walkData.walk.expected_km}km expected</span>
              </div>
            </div>
          )}
          
          {/* Display waypoints prominently */}
          {walkData.walk.waypoints && walkData.walk.waypoints.length > 0 && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="space-y-1">
                {walkData.walk.waypoints.slice(0, 3).map((waypoint, index) => {
                  const wpType = waypoint.type || 'checkpoint'
                  const iconConfig = waypointIcons[wpType as keyof typeof waypointIcons] || waypointIcons.checkpoint
                  const IconComponent = iconConfig.icon
                  return (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <IconComponent className="w-4 h-4" style={{ color: iconConfig.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-amber-700 truncate">
                          {waypoint.name || `${iconConfig.label} ${index + 1}`}
                        </div>
                      </div>
                    </div>
                  )
                })}
                {walkData.walk.waypoints.length > 3 && (
                  <div className="text-xs text-amber-700">+{walkData.walk.waypoints.length - 3} more</div>
                )}
              </div>
            </div>
          )}
          
          {/* Show fewer days on mobile */}
          <div className="space-y-1 text-xs">
            {walkData.activities.slice(0, isMobile ? 3 : 5).map((activity, index) => (
              <div 
                key={activity.id} 
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                onClick={() => handleDayClick(index)}
              >
                <div 
                  className="w-3 h-1 rounded flex-shrink-0"
                  style={{ backgroundColor: getColorForDay(index) }}
                />
                <span className="truncate">Day {index + 1}: {new Date(activity.date).toLocaleDateString()}{activity.activity_count > 1 ? ` (${activity.activity_count})` : ''}</span>
              </div>
            ))}
            {walkData.activities.length > (isMobile ? 3 : 5) && (
              <div 
                className="text-gray-500 cursor-pointer hover:text-gray-700 px-1 py-0.5"
                onClick={() => setIsDaysListPanelOpen(true)}
              >
                +{walkData.activities.length - (isMobile ? 3 : 5)} more days...
              </div>
            )}
          </div>
          <div className="mt-3 pt-2 border-t border-gray-200 space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-gray-500 flex-shrink-0"></div>
              <span>Start (solid dot, day colour)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full border-2 border-gray-500 bg-white flex-shrink-0"></div>
              <span>End (ring, day colour)</span>
            </div>
            {walkData.walk.waypoints && walkData.walk.waypoints.length > 0 && (
              <div className="flex items-center space-x-2">
                <svg className="w-3 h-3 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>Waypoints ({walkData.walk.waypoints.length})</span>
              </div>
            )}
            <div className="text-gray-500 mt-2 hidden sm:block">
              <span>Click routes or markers for details</span>
            </div>
          </div>
        </div>
      )}
                                                                                                 //

      {/* Floating stats bar at bottom - positioned above attribution with safe area */}
      {walkData && (
        <div 
          className="fixed left-1/2 transform -translate-x-1/2 bg-black/75 backdrop-blur-sm text-white rounded-full px-4 py-2 shadow-lg flex items-center space-x-3 text-sm z-10"
          style={{ 
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 2.5rem)'
          }}>
          {/* Days first */}
          <span className="font-medium">{walkData.activities.length}&nbsp;{walkData.activities.length === 1 ? 'day' : 'days'}</span>
          
          <div className="w-px h-4 bg-gray-500"></div>
          
          {/* Distance and progress */}
          <div className="flex items-center space-x-2">
            {(() => {
              const totalDistance = walkData.activities.reduce((sum, activity) => sum + activity.distance_km, 0)
              return (
                <>
                  <span className="font-medium">{totalDistance.toFixed(1)}km</span>
                  {walkData.walk.expected_km && (
                    <>
                      <span className="text-gray-300">of</span>
                      <span>{walkData.walk.expected_km}km</span>
                      <span className="text-green-400 font-medium">
                        ({Math.round((totalDistance / walkData.walk.expected_km) * 100)}%)
                      </span>
                    </>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* Day Details Panel */}
      <DayDetailsPanel
        isOpen={isDayDetailsPanelOpen}
        onClose={handleClosePanels}
        dayData={getSelectedDayData()}
      />

      {/* Days List Panel */}
      <DaysListPanel
        isOpen={isDaysListPanelOpen}
        onClose={() => setIsDaysListPanelOpen(false)}
        activities={walkData.activities}
        onDayClick={handleDayClick}
        getColorForDay={getColorForDay}
        waypoints={walkData.walk.waypoints}
      />
    </div>
  )
}

export default MapComponent